import React, { useCallback } from "react";
import { Entity } from "@common/types/entity";
import { TrashIcon, UploadIcon } from "@icons";
import { Button } from "@design-system/Button/Button";
import { ButtonWithLoader } from "@design-system/Button/ButtonWithLoader";
import { useScope } from "@state/scopeContext.lib";
import { setDropzoneRef } from "@lib/dropzoneRef";
import { useNativeInvoiceFormContext } from "./NativeInvoiceFormContext";
import { useNativeInvoiceLogoUpload } from "./useNativeInvoiceLogoUpload";
import { getInitials } from "./getInitials";
import styles from "./DesignSection.module.scss";

interface Props {
    selectedEntity?: Entity;
}

export const LogoUploadRow: React.FC<Props> = ({ selectedEntity }) => {
    const { state, setState } = useNativeInvoiceFormContext();
    const { workspaceId } = useScope();

    const handleLogoUploaded = useCallback(
        ({ path, publicUrl }: { path: string; publicUrl: string }) => {
            setState((prev) => ({
                ...prev,
                logoPath: path,
                logoPreviewUrl: publicUrl,
            }));
        },
        [setState],
    );

    const upload = useNativeInvoiceLogoUpload({
        workspaceId: workspaceId ?? "",
        entityId: selectedEntity?.id,
        onUploaded: handleLogoUploaded,
    });

    const canUpload = !!workspaceId && !!selectedEntity;

    return (
        <div className={styles.section}>
            <span className={styles.sectionLabel}>Logo</span>
            <div {...upload.rootProps} className={styles.logoRow}>
                <input
                    {...upload.inputProps}
                    className="d-none"
                    data-testid="native-invoice-logo-upload-input"
                    ref={(el) => setDropzoneRef(upload.inputProps, el)}
                />
                <ButtonWithLoader
                    loading={upload.isUploading}
                    variant="primary"
                    size="xl"
                    disabled={!canUpload}
                    onClick={upload.open}
                    className={styles.uploadButton}
                    leftIcon={
                        <span data-slot-box="" className={styles.logoIcon}>
                            {state.logoPreviewUrl ? (
                                <img
                                    src={encodeURI(state.logoPreviewUrl)}
                                    alt="Invoice logo"
                                    className={styles.logoIconImage}
                                />
                            ) : (
                                getInitials(state.from.businessName)
                            )}
                        </span>
                    }
                >
                    <UploadIcon />
                    Upload
                </ButtonWithLoader>
                {state.logoPreviewUrl && (
                    <Button
                        variant="primary"
                        size="xl"
                        iconOnly
                        aria-label="Remove logo"
                        onClick={() =>
                            setState((prev) => ({
                                ...prev,
                                logoPath: null,
                                logoPreviewUrl: null,
                            }))
                        }
                    >
                        <TrashIcon />
                    </Button>
                )}
            </div>
        </div>
    );
};
